import { cn, HorizontalLoader, Text } from '@youversion/design-system-components';
import { ClockStroked } from '@youversion/design-system-icons';
import { useTranslation } from 'react-i18next';

import type { UploadProgressBarStatus } from '@/constants/upload-workflow';
import { toProgressRange } from '@/utils/progress-utils';

type ProgressRange = import('@youversion/design-system-components').ProgressRange;

const STATUS_LABEL_KEYS: Record<
  UploadProgressBarStatus,
  | 'upload_widget.status_labels.uploading'
  | 'upload_widget.status_labels.processing'
  | 'upload_widget.status_labels.paused'
> = {
  uploading: 'upload_widget.status_labels.uploading',
  processing: 'upload_widget.status_labels.processing',
  paused: 'upload_widget.status_labels.paused',
};

const BAR_PROGRESS_CLASSNAME: Record<UploadProgressBarStatus, string> = {
  paused: 'bg-tertiary-light dark:bg-tertiary-dark rounded-0.25',
  uploading: 'bg-blue-30 rounded-0.25',
  processing: 'bg-blue-30 rounded-0.25',
};

export interface ContentStatusProgressBarMiniBarProps {
  progress: number;
  status: UploadProgressBarStatus;
}

export function ContentStatusProgressBarMiniBar({ progress, status }: ContentStatusProgressBarMiniBarProps) {
  const progressPercent: ProgressRange = status === 'processing' ? (100 as ProgressRange) : toProgressRange(progress);
  return (
    <>
      <progress className='sr-only' max={100} value={progressPercent} />
      <HorizontalLoader
        className='overflow-hidden rounded-0.25'
        height='1'
        progress={progressPercent}
        progressClassName={BAR_PROGRESS_CLASSNAME[status]}
      />
    </>
  );
}

export interface ContentStatusProgressBarProps {
  /** Progress from 0 to 1. For `processing`, the bar is shown at 100%. */
  progress: number;
  shouldTransformToMiniVersionOnMobile?: boolean;
  status: UploadProgressBarStatus;
}

/**
 * Status row with clock icon, label derived from status, and progress bar. Bar is always visible.
 *
 * @example
 * <ContentStatusProgressBar progress={0.5} status="uploading" />
 */
export function ContentStatusProgressBar({
  progress,
  status,
  shouldTransformToMiniVersionOnMobile,
}: ContentStatusProgressBarProps) {
  const { t } = useTranslation('common');
  const label = t(STATUS_LABEL_KEYS[status]) as string;
  const progressPercent: ProgressRange = status === 'processing' ? (100 as ProgressRange) : toProgressRange(progress);

  return (
    <div className='flex flex-1 min-w-0 flex-col gap-1'>
      <div className='flex items-center gap-0.5'>
        <ClockStroked aria-hidden={true} className='shrink-0 text-black dark:text-white' size='16' />
        <Text className='text-[13px] font-medium leading-default' size='sm' variant='label'>
          {label}
        </Text>
      </div>
      <div className={cn('min-w-0', { 'hidden md:flex': shouldTransformToMiniVersionOnMobile })}>
        <progress className='sr-only' max={100} value={progressPercent} />
        <HorizontalLoader
          className='overflow-hidden rounded-0.25'
          height='1'
          progress={progressPercent}
          progressClassName={BAR_PROGRESS_CLASSNAME[status]}
        />
      </div>
    </div>
  );
}

ContentStatusProgressBar.MiniBar = ContentStatusProgressBarMiniBar;
